import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import ImageTestimonials from './components/ImageTestimonials';
import VideoTestimonials from './components/VideoTestimonials';
import './App.css';

const { FiCheck, FiStar, FiTrendingUp, FiUsers, FiClock, FiTarget, FiArrowRight, FiGift, FiShield, FiZap, FiDollarSign, FiCalendar, FiAward, FiPlay, FiChevronDown, FiX, FiVideo, FiAlertCircle } = FiIcons;

function App() {
  const [selectedPlan, setSelectedPlan] = useState('platinum');
  const [showFAQ, setShowFAQ] = useState({});
  const [roiAmount, setRoiAmount] = useState(25000);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [showBonusModal, setShowBonusModal] = useState(false);
  const [showMembershipSelection, setShowMembershipSelection] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowStickyBar(window.scrollY > 800);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleFAQ = (index) => {
    setShowFAQ(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const handleSilverClick = () => {
    window.location.href = 'https://abdul.cc/onelakh';
  };

  const handlePlatinumClick = () => {
    window.location.href = 'http://razorpay.me/@abdulhakkima';
  };

  const handleApplicableProductClick = () => {
    setShowMembershipSelection(true);
  };

  const handleMembershipChoice = (membership) => {
    if (membership === 'silver') {
      handleSilverClick();
    } else {
      handlePlatinumClick();
    }
  };

  const roiResults = {
    25000: 300000,
    50000: 600000,
    100000: 1200000
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Membership Selection Modal */}
      <AnimatePresence>
        {showMembershipSelection && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 z-[110] flex items-center justify-center p-4"
            onClick={() => setShowMembershipSelection(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-gradient-to-br from-slate-900 to-slate-800 border-2 border-blue-500 rounded-2xl p-4 sm:p-6 md:p-8 max-w-3xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center mb-6 sm:mb-8">
                <h3 className="text-2xl sm:text-3xl font-bold text-white mb-3 sm:mb-4">
                  Choose Your Membership
                </h3>
                <p className="text-gray-300 text-base sm:text-lg">
                  Select which membership you'd like to join
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                {/* Silver Option */}
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleMembershipChoice('silver')}
                  className="bg-gradient-to-br from-yellow-600 to-yellow-700 hover:from-yellow-500 hover:to-yellow-600 text-white p-6 sm:p-8 rounded-xl border-2 border-yellow-400 transition-all"
                >
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                    <SafeIcon icon={FiPlay} className="text-2xl sm:text-3xl" />
                    <h4 className="text-xl sm:text-2xl font-bold text-center sm:text-left">Silver Membership</h4>
                  </div>
                  <div className="text-3xl sm:text-4xl font-bold mb-2">₹7,997</div>
                  <p className="text-yellow-100 mb-3 sm:mb-4 text-sm sm:text-base">Perfect for beginners</p>
                  <div className="flex items-center justify-center gap-2 text-yellow-100 text-sm sm:text-base">
                    <SafeIcon icon={FiArrowRight} />
                    <span>Start Your Journey</span>
                  </div>
                </motion.button>

                {/* Platinum Option */}
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleMembershipChoice('platinum')}
                  className="bg-gradient-to-br from-blue-600 to-purple-700 hover:from-blue-500 hover:to-purple-600 text-white p-6 sm:p-8 rounded-xl border-2 border-blue-400 transition-all relative overflow-hidden"
                >
                  <div className="absolute top-2 right-2 bg-orange-500 text-white text-xs px-2 sm:px-3 py-1 rounded-full font-semibold">
                    RECOMMENDED
                  </div>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3 mb-3 sm:mb-4 mt-6 sm:mt-0">
                    <SafeIcon icon={FiStar} className="text-2xl sm:text-3xl" />
                    <h4 className="text-xl sm:text-2xl font-bold text-center sm:text-left">Platinum Membership</h4>
                  </div>
                  <div className="text-3xl sm:text-4xl font-bold mb-2">₹3,00,000</div>
                  <p className="text-xs sm:text-sm text-blue-200 mb-2">+ GST</p>
                  <p className="text-blue-100 mb-3 sm:mb-4 text-sm sm:text-base">Best for serious builders</p>
                  <div className="flex items-center justify-center gap-2 text-blue-100 text-sm sm:text-base">
                    <SafeIcon icon={FiArrowRight} />
                    <span>Scale Faster</span>
                  </div>
                </motion.button>
              </div>

              <button
                onClick={() => setShowMembershipSelection(false)}
                className="w-full bg-slate-700 hover:bg-slate-600 text-white py-3 rounded-xl font-semibold transition-all text-sm sm:text-base"
              >
                Cancel
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bonus Workshop Modal */}
      <AnimatePresence>
        {showBonusModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-4"
            onClick={() => setShowBonusModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-gradient-to-br from-orange-900/90 to-red-900/90 border-2 border-orange-500 rounded-2xl p-4 sm:p-6 md:p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center mb-4 sm:mb-6">
                <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-full inline-block mb-3 sm:mb-4 font-bold text-base sm:text-xl">
                  🔥 Live-Only Special Offer 🔥
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4 sm:mb-6">
                  High Ticket 3 Days Workshop FREE
                </h3>
              </div>

              <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
                <div className="flex items-start gap-2 sm:gap-3 bg-white/10 p-3 sm:p-4 rounded-lg">
                  <SafeIcon icon={FiTarget} className="text-orange-400 text-lg sm:text-xl mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-white font-semibold text-sm sm:text-base">This is a Live-Only Offer</p>
                  </div>
                </div>

                <div className="flex items-start gap-2 sm:gap-3 bg-white/10 p-3 sm:p-4 rounded-lg">
                  <SafeIcon icon={FiZap} className="text-orange-400 text-lg sm:text-xl mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-white text-sm sm:text-base">Once payment is completed, you will get immediate access to the courses</p>
                  </div>
                </div>

                <div className="flex items-start gap-2 sm:gap-3 bg-white/10 p-3 sm:p-4 rounded-lg">
                  <SafeIcon icon={FiCalendar} className="text-orange-400 text-lg sm:text-xl mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-white text-sm sm:text-base">3 Days Live Training will happen at the end of this month</p>
                  </div>
                </div>

                <div className="flex items-start gap-2 sm:gap-3 bg-white/10 p-3 sm:p-4 rounded-lg">
                  <SafeIcon icon={FiArrowRight} className="text-orange-400 text-lg sm:text-xl mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-white text-sm sm:text-base">If you miss this month's live, you can attend the live training next month</p>
                  </div>
                </div>

                <div className="flex items-start gap-2 sm:gap-3 bg-white/10 p-3 sm:p-4 rounded-lg">
                  <SafeIcon icon={FiVideo} className="text-orange-400 text-lg sm:text-xl mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-white text-sm sm:text-base">Recordings will be shared only with live attendees</p>
                  </div>
                </div>
              </div>

              <button
                onClick={handleApplicableProductClick}
                className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white py-3 sm:py-4 rounded-xl font-bold text-sm sm:text-lg transition-all mb-3 sm:mb-4"
              >
                🎁 Applicable Product: Selected Product Only - FREE
              </button>

              <button
                onClick={() => setShowBonusModal(false)}
                className="w-full bg-white hover:bg-gray-100 text-orange-600 py-3 sm:py-4 rounded-xl font-semibold text-sm sm:text-lg transition-all"
              >
                Got It!
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Sticky CTA Bar */}
      <AnimatePresence>
        {showStickyBar && (
          <motion.div
            initial={{ y: -100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -100, opacity: 0 }}
            className="fixed top-0 left-0 right-0 bg-gradient-to-r from-blue-600 to-purple-700 z-50 p-3 sm:p-4 shadow-2xl"
          >
            <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-2 sm:gap-3">
              <div className="text-white text-center md:text-left text-sm sm:text-base">
                <span className="font-semibold">Don't wait another year.</span>
                <span className="ml-2 text-blue-100">Choose your path today.</span>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full md:w-auto">
                <button 
                  onClick={handleSilverClick}
                  className="bg-yellow-500 hover:bg-yellow-400 text-black px-4 sm:px-6 py-2 rounded-lg font-semibold transition-all text-sm sm:text-base whitespace-nowrap"
                >
                  Silver Membership
                </button>
                <button 
                  onClick={handlePlatinumClick}
                  className="bg-white hover:bg-gray-100 text-blue-600 px-4 sm:px-6 py-2 rounded-lg font-semibold transition-all text-sm sm:text-base whitespace-nowrap"
                >
                  Platinum (Recommended)
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden px-4 sm:px-6 py-12 sm:py-0">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-purple-900/20 to-slate-900/40"></div>
        <div className="relative z-10 max-w-6xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-white mb-4 sm:mb-6 leading-tight px-2">
              Don't Build Your Business
              <span className="bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent"> Alone </span>
              for Another Year.
            </h1>
            
            <p className="text-lg sm:text-xl md:text-2xl text-gray-300 mb-3 sm:mb-4 max-w-4xl mx-auto px-2">
              Choose the Path That Guarantees Progress.
            </p>
            
            <p className="text-base sm:text-lg text-gray-400 mb-6 sm:mb-8 max-w-3xl mx-auto px-2">
              Two memberships. One ecosystem. 
              Designed to help you execute, earn, and grow with confidence.
            </p>

            {/* Bonus Banner */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="bg-gradient-to-r from-orange-600 to-red-600 border-2 border-orange-400 rounded-xl p-4 sm:p-6 mb-8 sm:mb-12 max-w-3xl mx-auto cursor-pointer hover:scale-105 transition-transform"
              onClick={() => setShowBonusModal(true)}
            >
              <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                <SafeIcon icon={FiGift} className="text-white text-2xl sm:text-3xl" />
                <h3 className="text-xl sm:text-2xl font-bold text-white text-center">EXCLUSIVE BONUS</h3>
                <SafeIcon icon={FiGift} className="text-white text-2xl sm:text-3xl hidden sm:block" />
              </div>
              <p className="text-white text-base sm:text-lg font-semibold mb-2 px-2">
                🔥 Join Silver or Platinum & Get High Ticket 3 Days Workshop FREE! 🔥
              </p>
              <p className="text-orange-100 text-xs sm:text-sm">
                Click to see full details →
              </p>
            </motion.div>
          </motion.div>

          {/* Trust Highlights */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="grid grid-cols-2 md:grid-cols-5 gap-3 sm:gap-4 md:gap-6 mb-8 sm:mb-12 max-w-5xl mx-auto"
          >
            {[
              { icon: FiShield, text: "Lifetime Access" },
              { icon: FiUsers, text: "Weekly Live Coaching for Life" },
              { icon: FiZap, text: "AI + Automation Systems" },
              { icon: FiDollarSign, text: "Referral Income Opportunity" },
              { icon: FiGift, text: "1-Day FREE Offline Workshop (Worth ₹9,997)" }
            ].map((item, index) => (
              <div key={index} className="flex flex-col items-center text-center">
                <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-2 sm:p-3 rounded-full mb-2 sm:mb-3">
                  <SafeIcon icon={item.icon} className="text-white text-base sm:text-xl" />
                </div>
                <span className="text-gray-300 text-xs sm:text-sm leading-tight">{item.text}</span>
              </div>
            ))}
          </motion.div>

          {/* Primary CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center px-4"
          >
            <button 
              onClick={handlePlatinumClick}
              className="group w-full sm:w-auto bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold text-base sm:text-lg shadow-2xl transition-all transform hover:scale-105 flex items-center justify-center gap-2 sm:gap-3"
            >
              <span className="bg-blue-500 p-1 rounded-full">
                <SafeIcon icon={FiStar} className="text-white text-sm sm:text-base" />
              </span>
              <span className="text-sm sm:text-base">Upgrade to Platinum (Recommended)</span>
              <SafeIcon icon={FiArrowRight} className="group-hover:translate-x-1 transition-transform hidden sm:block" />
            </button>
            
            <button 
              onClick={handleSilverClick}
              className="w-full sm:w-auto bg-yellow-500 hover:bg-yellow-400 text-black px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold text-base sm:text-lg shadow-xl transition-all transform hover:scale-105 flex items-center justify-center gap-2 sm:gap-3"
            >
              <span className="bg-yellow-600 p-1 rounded-full">
                <SafeIcon icon={FiPlay} className="text-white text-sm sm:text-base" />
              </span>
              <span className="text-sm sm:text-base">Start with Silver</span>
            </button>
          </motion.div>
        </div>
      </section>

      {/* Emotional Problem Section */}
      <section className="py-12 sm:py-16 md:py-20 bg-slate-800/50 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10 sm:mb-16"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6 sm:mb-8 px-2">
              Why Smart Business Owners Still Feel Stuck
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8 mb-10 sm:mb-16">
            {[
              "I keep learning, but I don't move forward consistently",
              "No one checks my work or corrects mistakes",
              "Income is unpredictable month after month",
              "I buy tools but don't fully use them",
              "I feel like I'm doing everything alone"
            ].map((pain, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gradient-to-br from-red-900/20 to-red-800/20 border border-red-700/30 p-4 sm:p-6 rounded-xl"
              >
                <p className="text-red-200 text-base sm:text-lg italic">"{pain}"</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center bg-gradient-to-r from-blue-900/30 to-purple-900/30 border border-blue-700/30 p-6 sm:p-8 rounded-2xl"
          >
            <p className="text-xl sm:text-2xl md:text-3xl text-white font-semibold mb-3 sm:mb-4 px-2">
              Your problem is not knowledge.
            </p>
            <p className="text-lg sm:text-xl text-gray-300 px-2">
              It's the absence of long-term support, systems, and accountability.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Solution Overview */}
      <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10 sm:mb-16"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6 sm:mb-8 px-2">
              One Ecosystem. Two Ways to Enter.
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8 max-w-4xl mx-auto">
              <div className="bg-gradient-to-br from-blue-900/30 to-blue-800/30 border border-blue-700/30 p-6 sm:p-8 rounded-2xl">
                <h3 className="text-xl sm:text-2xl font-bold text-blue-400 mb-3 sm:mb-4">Platinum</h3>
                <p className="text-gray-300 text-sm sm:text-base">Full execution, scale, income & 3-year safety net</p>
              </div>
              <div className="bg-gradient-to-br from-yellow-900/30 to-yellow-800/30 border border-yellow-700/30 p-6 sm:p-8 rounded-2xl">
                <h3 className="text-xl sm:text-2xl font-bold text-yellow-400 mb-3 sm:mb-4">Silver</h3>
                <p className="text-gray-300 text-sm sm:text-base">Strong foundation, guidance & entry</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Image Testimonials Section */}
      <ImageTestimonials />

      {/* Comparison Table */}
      <section className="py-12 sm:py-16 md:py-20 bg-slate-800/50 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10 sm:mb-16"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6 sm:mb-8 px-2">
              Compare & Choose the Smarter Path
            </h2>
          </motion.div>

          <div className="overflow-x-auto -mx-4 sm:mx-0">
            <div className="inline-block min-w-full align-middle">
              <div className="overflow-hidden">
                <table className="min-w-full bg-slate-900/50 rounded-none sm:rounded-2xl border-y sm:border border-slate-700">
                  <thead>
                    <tr className="bg-gradient-to-r from-slate-800 to-slate-700">
                      <th className="text-left p-3 sm:p-4 md:p-6 text-white font-semibold text-xs sm:text-sm md:text-base min-w-[120px] sm:min-w-0">Feature</th>
                      <th className="text-center p-3 sm:p-4 md:p-6 text-yellow-400 font-semibold text-xs sm:text-sm md:text-base min-w-[100px] sm:min-w-0">🟡 Silver</th>
                      <th className="text-center p-3 sm:p-4 md:p-6 text-blue-400 font-semibold text-xs sm:text-sm md:text-base min-w-[100px] sm:min-w-0">🔵 Platinum</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ["Lifetime Membership Access", true, true],
                      ["16 AI Apps (₹80,000 value)", true, true],
                      ["Lifetime Weekly Live Coaching", true, true],
                      ["Daily Coaching Calls", "1 Month", "12 Months"],
                      ["Diamond Passive Income Membership", false, "✅ Lifetime"],
                      ["Agency Personal Accountability", false, "✅ 3 Months"],
                      ["Premium Softwares (14 Tools)", false, "✅ 3 Years (We Pay for You)"],
                      ["Referral Commission", "10%", "25% Recurring"],
                      ["Free Silver Members You Can Add", "1", "4"],
                      ["FREE Offline Workshop (₹9,997)", true, true],
                      ["High Ticket 3 Days Workshop", "✅ FREE", "✅ FREE"],
                      ["Best For", "Beginners", "Serious Builders & Scalers"]
                    ].map(([feature, silver, platinum], index) => (
                      <tr key={index} className="border-t border-slate-700">
                        <td className="p-3 sm:p-4 md:p-6 text-gray-300 text-xs sm:text-sm md:text-base">{feature}</td>
                        <td className="p-3 sm:p-4 md:p-6 text-center text-xs sm:text-sm md:text-base">
                          {typeof silver === 'boolean' ? (
                            silver ? <SafeIcon icon={FiCheck} className="text-green-400 mx-auto text-base sm:text-lg" /> : <SafeIcon icon={FiX} className="text-red-400 mx-auto text-base sm:text-lg" />
                          ) : (
                            <span className="text-yellow-300">{silver}</span>
                          )}
                        </td>
                        <td className="p-3 sm:p-4 md:p-6 text-center text-xs sm:text-sm md:text-base">
                          {typeof platinum === 'boolean' ? (
                            platinum ? <SafeIcon icon={FiCheck} className="text-green-400 mx-auto text-base sm:text-lg" /> : <SafeIcon icon={FiX} className="text-red-400 mx-auto text-base sm:text-lg" />
                          ) : (
                            <span className="text-blue-300">{platinum}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-10 md:gap-12">
            {/* Silver Pricing */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-yellow-900/20 to-yellow-800/20 border-2 border-yellow-500/30 p-6 sm:p-8 rounded-2xl"
            >
              <div className="text-center mb-6 sm:mb-8">
                <h3 className="text-2xl sm:text-3xl font-bold text-yellow-400 mb-3 sm:mb-4">🟡 Silver Membership</h3>
                <div className="mb-4 sm:mb-6">
                  <div className="text-3xl sm:text-4xl font-bold text-white mb-2">₹7,997</div>
                  <div className="text-yellow-300 text-sm sm:text-base">(Inclusive GST)</div>
                  <div className="text-xs sm:text-sm text-gray-400 line-through">Normally ₹15,997 + GST / year</div>
                </div>
              </div>
              
              <div className="mb-6 sm:mb-8">
                <h4 className="text-lg sm:text-xl font-semibold text-white mb-3 sm:mb-4">Best if:</h4>
                <ul className="space-y-2 sm:space-y-3">
                  {["You want guidance", "You're just starting", "You want low-risk entry"].map((item, index) => (
                    <li key={index} className="flex items-center gap-2 sm:gap-3 text-gray-300 text-sm sm:text-base">
                      <SafeIcon icon={FiCheck} className="text-yellow-400 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-orange-600/30 border border-orange-500 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6">
                <p className="text-orange-200 text-xs sm:text-sm font-semibold text-center">
                  🎁 + FREE High Ticket 3 Days Workshop
                </p>
              </div>
              
              <button 
                onClick={handleSilverClick}
                className="w-full bg-yellow-500 hover:bg-yellow-400 text-black py-3 sm:py-4 rounded-xl font-semibold text-base sm:text-lg transition-all transform hover:scale-105"
              >
                Start with Silver
              </button>
            </motion.div>

            {/* Platinum Pricing */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-blue-900/20 to-purple-900/20 border-2 border-blue-500/50 p-6 sm:p-8 rounded-2xl relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 bg-gradient-to-l from-blue-500 to-purple-600 text-white px-3 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm font-semibold">
                RECOMMENDED
              </div>
              
              <div className="text-center mb-6 sm:mb-8 mt-6 sm:mt-0">
                <h3 className="text-2xl sm:text-3xl font-bold text-blue-400 mb-3 sm:mb-4">🔵 Platinum Membership</h3>
                <div className="mb-4 sm:mb-6">
                  <div className="text-3xl sm:text-4xl font-bold text-white mb-2">₹3,00,000</div>
                  <div className="text-blue-300 text-sm sm:text-base">+ GST</div>
                  <div className="text-xs sm:text-sm text-gray-400 line-through">Normally ₹5,00,000 + GST</div>
                </div>
              </div>
              
              <div className="mb-6 sm:mb-8">
                <h4 className="text-lg sm:text-xl font-semibold text-white mb-3 sm:mb-4">Best if:</h4>
                <ul className="space-y-2 sm:space-y-3">
                  {["You want execution, not experiments", "You want accountability & speed", "You want ROI, not motivation", "You need 3 years of software support"].map((item, index) => (
                    <li key={index} className="flex items-center gap-2 sm:gap-3 text-gray-300 text-sm sm:text-base">
                      <SafeIcon icon={FiCheck} className="text-blue-400 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-orange-600/30 border border-orange-500 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6">
                <p className="text-orange-200 text-xs sm:text-sm font-semibold text-center">
                  🎁 + FREE High Ticket 3 Days Workshop
                </p>
              </div>
              
              <button 
                onClick={handlePlatinumClick}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 text-white py-3 sm:py-4 rounded-xl font-semibold text-base sm:text-lg transition-all transform hover:scale-105"
              >
                Make Deposit (Min. ₹25,000)
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Video Testimonials Section */}
      <VideoTestimonials />

      {/* 3 Years Matters Section */}
      <section className="py-12 sm:py-16 md:py-20 bg-slate-800/50 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10 sm:mb-16"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6 sm:mb-8 px-2">
              Why 3 Years of Premium Software Access Changes Everything
            </h2>
            <p className="text-lg sm:text-xl text-gray-300 mb-8 sm:mb-12 max-w-4xl mx-auto px-2">
              Most people quit not because they fail — but because recurring software costs drain their profits before they can scale.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-10 md:gap-12 mb-10 sm:mb-16">
            <div>
              <h3 className="text-xl sm:text-2xl font-bold text-blue-400 mb-6 sm:mb-8">With 3 Years You Get:</h3>
              <div className="space-y-4 sm:space-y-6">
                {[
                  "Zero software costs for 36 months - pure profit retention",
                  "Full business cycle: setup → launch → optimize → scale → dominate",
                  "Time to test, fail safely, and perfect your systems",
                  "No pressure to \"rush results\" or worry about renewals",
                  "Systems built once, used repeatedly without monthly fees",
                  "14 premium tools worth ₹2,00,000+ annually - all paid for you"
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-3 sm:gap-4">
                    <div className="bg-blue-500 p-1 rounded-full mt-1 flex-shrink-0">
                      <SafeIcon icon={FiCheck} className="text-white text-xs sm:text-sm" />
                    </div>
                    <span className="text-gray-300 text-sm sm:text-base md:text-lg">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-purple-900/30 to-blue-900/30 border border-purple-700/30 p-6 sm:p-8 rounded-2xl">
              <div className="text-center mb-4 sm:mb-6">
                <SafeIcon icon={FiCalendar} className="text-purple-400 text-4xl sm:text-5xl mx-auto mb-4 sm:mb-6" />
                <p className="text-lg sm:text-xl text-white font-semibold mb-3 sm:mb-4 px-2">
                  Short programs teach information.
                </p>
                <p className="text-xl sm:text-2xl text-purple-400 font-bold mb-4 sm:mb-6 px-2">
                  3 years builds empire-level transformation.
                </p>
              </div>
              <div className="bg-blue-900/40 border border-blue-600/30 p-3 sm:p-4 rounded-xl">
                <p className="text-blue-200 text-xs sm:text-sm mb-2">💡 <strong>Real Math:</strong></p>
                <p className="text-gray-300 text-xs sm:text-sm">
                  14 tools × ₹15,000/year average = ₹2,10,000/year<br/>
                  × 3 years = <strong className="text-green-400">₹6,30,000 in software costs we cover</strong>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Referral Income Section */}
      <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10 sm:mb-16"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6 sm:mb-8 px-2">
              Recover Your Investment Through Referrals
            </h2>
            <p className="text-lg sm:text-xl text-gray-400">(Optional, Not Compulsory)</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-10 md:gap-12">
            {/* Silver Referral */}
            <div className="bg-gradient-to-br from-yellow-900/20 to-yellow-800/20 border border-yellow-700/30 p-6 sm:p-8 rounded-2xl">
              <h3 className="text-xl sm:text-2xl font-bold text-yellow-400 mb-4 sm:mb-6">Silver Referral Logic</h3>
              <div className="space-y-3 sm:space-y-4">
                {[
                  "Add 1 Silver member FREE",
                  "Earn 10% recurring commission on upgrades",
                  "Bonus income, not main goal"
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-2 sm:gap-3">
                    <SafeIcon icon={FiCheck} className="text-yellow-400 flex-shrink-0" />
                    <span className="text-gray-300 text-sm sm:text-base">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Platinum Referral */}
            <div className="bg-gradient-to-br from-blue-900/20 to-purple-900/20 border border-blue-700/30 p-6 sm:p-8 rounded-2xl">
              <h3 className="text-xl sm:text-2xl font-bold text-blue-400 mb-4 sm:mb-6">Platinum Referral Logic (POWERFUL)</h3>
              <div className="space-y-3 sm:space-y-4 mb-4 sm:mb-6">
                {[
                  "Add 4 Silver members FREE",
                  "Earn 25% recurring commission",
                  "Recover Platinum investment",
                  "Membership effectively pays for itself",
                  "Build passive income while scaling"
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-2 sm:gap-3">
                    <SafeIcon icon={FiCheck} className="text-blue-400 flex-shrink-0" />
                    <span className="text-gray-300 text-sm sm:text-base">{item}</span>
                  </div>
                ))}
              </div>
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-3 sm:p-4 rounded-xl">
                <p className="text-white font-semibold text-center text-sm sm:text-base">
                  Platinum turns members into partners, not just learners.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ROI Calculator */}
      <section className="py-12 sm:py-16 md:py-20 bg-slate-800/50 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10 sm:mb-16"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6 sm:mb-8 px-2">
              What If This Membership Helps You Earn Just a Little More?
            </h2>
            <p className="text-lg sm:text-xl text-gray-300 mb-3 sm:mb-4 px-2">
              Conservative projections based on implementation
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-slate-900/50 rounded-2xl p-4 sm:p-6 md:p-8 mb-6 sm:mb-8 overflow-x-auto">
              <table className="w-full min-w-[300px]">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-3 sm:py-4 text-gray-300 font-semibold text-sm sm:text-base">Monthly Improvement</th>
                    <th className="text-right py-3 sm:py-4 text-gray-300 font-semibold text-sm sm:text-base">12-Month Result</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(roiResults).map(([monthly, yearly], index) => (
                    <tr key={index} className="border-b border-slate-700/50">
                      <td className="py-3 sm:py-4 text-white text-sm sm:text-base">{formatCurrency(monthly)} / month</td>
                      <td className="py-3 sm:py-4 text-right text-green-400 font-semibold text-sm sm:text-base">{formatCurrency(yearly)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="text-center bg-gradient-to-r from-green-900/30 to-emerald-900/30 border border-green-700/30 p-6 sm:p-8 rounded-2xl mb-6 sm:mb-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 items-center">
                <div>
                  <p className="text-lg sm:text-xl text-white mb-2 px-2">Platinum Cost: <span className="font-bold">₹3,00,000 + GST</span></p>
                  <p className="text-xl sm:text-2xl text-green-400 font-bold px-2">Potential ROI: 4× to 12× within 12 months</p>
                </div>
                <div className="flex justify-center">
                  <SafeIcon icon={FiTrendingUp} className="text-green-400 text-5xl sm:text-6xl" />
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-blue-900/30 to-purple-900/30 border border-blue-700/30 p-4 sm:p-6 rounded-xl">
              <h4 className="text-lg sm:text-xl font-bold text-white mb-3 sm:mb-4 text-center">Plus: Software Savings</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 text-center">
                <div>
                  <p className="text-gray-300 text-sm sm:text-base">Year 1 Software Value</p>
                  <p className="text-xl sm:text-2xl font-bold text-blue-400">₹2,10,000</p>
                </div>
                <div>
                  <p className="text-gray-300 text-sm sm:text-base">3-Year Software Value</p>
                  <p className="text-xl sm:text-2xl font-bold text-purple-400">₹6,30,000</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cost of Inaction */}
      <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-8 sm:mb-12 px-2">
              The Hidden Cost of Doing Nothing
            </h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 mb-10 sm:mb-16">
              {[
                { text: "Another year of confusion", cost: "₹50,000 in lost opportunities" },
                { text: "Another year of software subscriptions", cost: "₹2,10,000 out of pocket" },
                { text: "Another year of unstable income", cost: "₹3,00,000+ unrealized revenue" },
                { text: "Another year alone", cost: "Priceless time & mental peace" }
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gradient-to-br from-red-900/20 to-red-800/20 border border-red-700/30 p-4 sm:p-6 rounded-xl"
                >
                  <SafeIcon icon={FiClock} className="text-red-400 text-2xl sm:text-3xl mx-auto mb-3 sm:mb-4" />
                  <p className="text-red-200 font-semibold mb-2 text-sm sm:text-base">{item.text}</p>
                  <p className="text-red-300 text-xs sm:text-sm">{item.cost}</p>
                </motion.div>
              ))}
            </div>

            <div className="bg-gradient-to-r from-orange-900/30 to-red-900/30 border border-orange-700/30 p-6 sm:p-8 rounded-2xl max-w-3xl mx-auto">
              <p className="text-xl sm:text-2xl text-white font-semibold mb-3 sm:mb-4 px-2">
                Not joining is also a decision —
              </p>
              <p className="text-lg sm:text-xl text-orange-300 mb-3 sm:mb-4 px-2">
                and it has a cost of ₹5,60,000+ over the next year.
              </p>
              <p className="text-gray-300 text-xs sm:text-sm px-2">
                (Software costs + lost revenue + opportunity cost)
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Payment Options */}
      <section className="py-12 sm:py-16 md:py-20 bg-slate-800/50 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10 sm:mb-16"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6 sm:mb-8 px-2">
              Platinum Payment Options
            </h2>
            <p className="text-lg sm:text-xl text-gray-300 px-2">
              Flexible payment plans to suit your cash flow
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-10 md:gap-12 mb-8 sm:mb-12">
            {/* Option 1 */}
            <div className="bg-gradient-to-br from-green-900/20 to-emerald-900/20 border-2 border-green-500/30 p-6 sm:p-8 rounded-2xl">
              <div className="text-center mb-4 sm:mb-6">
                <div className="bg-green-500 text-white px-3 sm:px-4 py-1 sm:py-2 rounded-full inline-block mb-3 sm:mb-4 font-semibold text-sm sm:text-base">
                  Option 1 – Best Value
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-white mb-3 sm:mb-4">Pay ₹3,00,000 + GST Full</h3>
                <div className="flex items-center justify-center gap-2 sm:gap-3 text-green-400 mb-3 sm:mb-4">
                  <SafeIcon icon={FiCheck} />
                  <span className="text-lg sm:text-xl font-semibold">Save ₹2,00,000 instantly</span>
                </div>
                <p className="text-gray-300 text-xs sm:text-sm">Start immediately with all benefits unlocked</p>
              </div>
              <button 
                onClick={handlePlatinumClick}
                className="w-full bg-green-600 hover:bg-green-500 text-white py-3 sm:py-4 rounded-xl font-semibold text-base sm:text-lg transition-all"
              >
                Pay Full Amount
              </button>
            </div>

            {/* Option 2 */}
            <div className="bg-gradient-to-br from-blue-900/20 to-blue-800/20 border border-blue-700/30 p-6 sm:p-8 rounded-2xl">
              <div className="text-center mb-4 sm:mb-6">
                <div className="bg-blue-500 text-white px-3 sm:px-4 py-1 sm:py-2 rounded-full inline-block mb-3 sm:mb-4 font-semibold text-sm sm:text-base">
                  Option 2 – Flexible Start
                </div>
                <div className="space-y-2 sm:space-y-3 text-left">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <SafeIcon icon={FiDollarSign} className="text-blue-400 flex-shrink-0" />
                    <span className="text-white text-sm sm:text-base">Minimum Deposit: ₹25,000 Today</span>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3">
                    <SafeIcon icon={FiArrowRight} className="text-blue-400 flex-shrink-0" />
                    <span className="text-gray-300 text-sm sm:text-base">Discuss custom payment plan</span>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3">
                    <SafeIcon icon={FiTarget} className="text-blue-400 flex-shrink-0" />
                    <span className="text-white font-semibold text-sm sm:text-base">Complete payment in agreed installments</span>
                  </div>
                </div>
              </div>
              <button 
                onClick={handlePlatinumClick}
                className="w-full bg-blue-600 hover:bg-blue-500 text-white py-3 sm:py-4 rounded-xl font-semibold text-base sm:text-lg transition-all"
              >
                Make Deposit (₹25,000)
              </button>
            </div>
          </div>

          <div className="text-center">
            <div className="bg-gradient-to-r from-purple-900/30 to-blue-900/30 border border-purple-700/30 p-4 sm:p-6 rounded-xl max-w-2xl mx-auto">
              <h4 className="text-lg sm:text-xl font-semibold text-white mb-3 sm:mb-4">Additional Payment Facilities</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="flex items-center gap-2 sm:gap-3 justify-center">
                  <SafeIcon icon={FiCheck} className="text-green-400 flex-shrink-0" />
                  <span className="text-gray-300 text-sm sm:text-base">Bank EMI Available</span>
                </div>
                <div className="flex items-center gap-2 sm:gap-3 justify-center">
                  <SafeIcon icon={FiCheck} className="text-green-400 flex-shrink-0" />
                  <span className="text-gray-300 text-sm sm:text-base">Credit Card Accepted</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl md:text-6xl font-bold text-white mb-6 sm:mb-8 px-2">
              Don't Choose Based on Price.
            </h2>
            <p className="text-xl sm:text-2xl md:text-3xl text-gray-300 mb-8 sm:mb-12 px-2">
              Choose Based on Where You Want to Be 3 Years from Now.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center max-w-2xl mx-auto">
              <button 
                onClick={handlePlatinumClick}
                className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold text-base sm:text-lg shadow-2xl transition-all transform hover:scale-105 flex items-center justify-center gap-2 sm:gap-3"
              >
                <SafeIcon icon={FiStar} />
                <span className="text-sm sm:text-base">Upgrade to Platinum (Recommended)</span>
              </button>
              
              <button 
                onClick={handleSilverClick}
                className="w-full sm:w-auto bg-yellow-500 hover:bg-yellow-400 text-black px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold text-base sm:text-lg shadow-xl transition-all transform hover:scale-105 flex items-center justify-center gap-2 sm:gap-3"
              >
                <SafeIcon icon={FiPlay} />
                <span className="text-sm sm:text-base">Start with Silver</span>
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 sm:py-16 md:py-20 bg-slate-800/50 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10 sm:mb-16"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6 sm:mb-8 px-2">
              Frequently Asked Questions
            </h2>
          </motion.div>

          <div className="space-y-3 sm:space-y-4">
            {[
              {
                question: "What if I don't get time?",
                answer: "This is built for busy owners — systems + accountability. Our 3-year access means you can pace yourself without pressure."
              },
              {
                question: "Can I upgrade later?",
                answer: "Yes, Silver members can upgrade to Platinum anytime. The price difference will be adjusted."
              },
              {
                question: "Is the offline workshop really free?",
                answer: "Yes. All members get the ₹9,997 offline workshop absolutely FREE."
              },
              {
                question: "What about the 3 Days High Ticket Workshop?",
                answer: "Both Silver and Platinum members get the High Ticket 3 Days Workshop absolutely FREE as a bonus. Live training happens at the end of each month with recordings shared to attendees."
              },
              {
                question: "Why 3 years of software access?",
                answer: "Real business transformation takes time. With 3 years, you can build, test, optimize, and scale without worrying about recurring software costs eating into your profits. That's ₹6,30,000 in software costs we cover for you."
              },
              {
                question: "Is Platinum refundable via referrals?",
                answer: "Yes! With just 4 successful referrals at 25% recurring commission, many members recover their full investment while building passive income."
              },
              {
                question: "What's the minimum deposit for Platinum?",
                answer: "You can start with a minimum deposit of ₹25,000 and discuss a custom payment plan that works for your cash flow."
              },
              {
                question: "Is this another course?",
                answer: "No. This is coaching + execution + accountability + 3 years of premium software access. It's a complete business ecosystem."
              },
              {
                question: "What happens after 3 years?",
                answer: "After 3 years, you'll have built systems, processes, and revenue streams that are self-sustaining. You can choose to renew software access or use alternatives by then."
              }
            ].map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-slate-900/50 border border-slate-700 rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full p-4 sm:p-6 text-left flex items-center justify-between hover:bg-slate-800/50 transition-all gap-3"
                >
                  <span className="text-white font-semibold text-base sm:text-lg pr-2">{faq.question}</span>
                  <SafeIcon 
                    icon={FiChevronDown} 
                    className={`text-gray-400 transition-transform flex-shrink-0 ${showFAQ[index] ? 'rotate-180' : ''}`} 
                  />
                </button>
                <AnimatePresence>
                  {showFAQ[index] && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="border-t border-slate-700"
                    >
                      <div className="p-4 sm:p-6 text-gray-300 text-base sm:text-lg">
                        {faq.answer}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 sm:py-12 bg-slate-900 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-gray-400 text-sm sm:text-base">
            © 2026 Funnelsdone.com Workshop Priority Offer. Transform your business with the right support system.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;